import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useRequestStore } from '../../store/requestStore';
import { useAuthStore } from '../../store/authStore';
import { RequestState, SURVEY_SERVICE_TYPES, SurveyorProfile } from '../../types';
import { PieChart, PIE_COLORS } from '../../components/PieChart';
import { AnimatedIcon, IconName } from '../../components/icons';
import { WEB_STATUS_COLORS } from '../../utils/statusPresentation';
import { apiClient } from '../../services/apiClient';
import { toNumber } from '../../utils/number';

const STATUS_META: { label: string; shortLabel: string; status: RequestState; color: string; icon: IconName }[] = [
  { label: 'Submitted', shortLabel: 'Submitted', status: RequestState.REQUEST_SUBMITTED, color: 'var(--sc-brand-primary)', icon: 'clipboard' },
  { label: 'Under Review', shortLabel: 'Review', status: RequestState.UNDER_REVIEW, color: 'var(--sc-warning)', icon: 'bell' },
  { label: 'Approved', shortLabel: 'Approved', status: RequestState.APPROVED, color: 'var(--sc-success)', icon: 'success' },
  { label: 'Assigned', shortLabel: 'Assigned', status: RequestState.ASSIGNED, color: '#7C3AED', icon: 'worker' },
  { label: 'Negotiating', shortLabel: 'Negotiating', status: RequestState.NEGOTIATING, color: '#EA580C', icon: 'chat' },
  { label: 'Confirmed', shortLabel: 'Confirmed', status: RequestState.CONFIRMED, color: 'var(--sc-success)', icon: 'folder' },
  { label: 'Closed', shortLabel: 'Closed', status: RequestState.CLOSED, color: 'var(--sc-text-secondary)', icon: 'target' },
  { label: 'Disputed', shortLabel: 'Disputed', status: RequestState.DISPUTED, color: 'var(--sc-error)', icon: 'warning' },
];

const STATUS_TABS = [
  { label: 'All', value: undefined as RequestState | undefined },
  ...STATUS_META.map((s) => ({ label: s.shortLabel, value: s.status })),
];

const STATUS_COLORS = WEB_STATUS_COLORS;

const getCompactStatusText = (status: string) => {
  switch (status) {
    case 'UNDER_REVIEW': return '🟡 Review';
    case 'ASSIGNED': return '🔵 Assigned';
    case 'NEGOTIATING': return '🟡 Negotiating';
    case 'CONFIRMED': return '🟢 Confirmed';
    case 'CLOSED': return '🟢 Closed';
    case 'REQUEST_SUBMITTED': return '🔵 Submitted';
    case 'APPROVED': return '🟢 Approved';
    case 'DISPUTED': return '🟠 Disputed';
    case 'REJECTED': return '🔴 Rejected';
    default: return status.replace(/_/g, ' ');
  }
};

const getSurveyorDisplayName = (surveyor?: SurveyorProfile | null) => (
  surveyor?.full_name ||
  `${surveyor?.first_name || ''} ${surveyor?.last_name || ''}`.trim() ||
  surveyor?.email ||
  'Surveyor'
);

export const ClientDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const { requests, isLoading, fetchRequests } = useRequestStore();
  const [activeFilter, setActiveFilter] = useState<RequestState | undefined>(undefined);
  const [serviceTypeFilter, setServiceTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchText, setSearchText] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [topSurveyors, setTopSurveyors] = useState<SurveyorProfile[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchRequests();
    apiClient.get('/surveyors/', { params: { verification_status: 'verified', page: 1 } })
      .then(({ data }) => {
        const list = (data.results ?? data ?? []) as SurveyorProfile[];
        setTopSurveyors(
          list
            .filter((surveyor) => surveyor.verification_status === 'APPROVED')
            .sort((a, b) =>
              toNumber(b.rating_average) - toNumber(a.rating_average) ||
              toNumber(b.total_jobs) - toNumber(a.total_jobs)
            )
            .slice(0, 4),
        );
      })
      .catch(() => setTopSurveyors([]));
  }, []);

  const filtered = requests.filter((r) => {
    if (activeFilter && r.status !== activeFilter) return false;
    if (searchText) {
      const q = searchText.toLowerCase();
      if (!r.service_type?.toLowerCase().includes(q) && 
          !r.address?.toLowerCase().includes(q) && 
          !r.description?.toLowerCase().includes(q)) return false;
    }
    if (serviceTypeFilter && r.service_type !== serviceTypeFilter) return false;
    if (dateFrom && new Date(r.created_at) < new Date(dateFrom)) return false;
    if (dateTo && new Date(r.created_at) > new Date(dateTo + 'T23:59:59')) return false;
    return true;
  });

  const countFor = (status?: RequestState) =>
    status === undefined ? requests.length : requests.filter((r) => r.status === status).length;

  const formatDate = (d: string) => 
    new Date(d).toLocaleDateString('en-ZW', { day: 'numeric', month: 'short', year: 'numeric' });

  const analytics = useMemo(() => {
    const totalSpend = requests.reduce((sum, r) => sum + (r.deal_value || 0), 0);
    const confirmedCount = requests.filter((r) => r.status === RequestState.CONFIRMED || r.status === RequestState.CLOSED).length;
    const avgTurnaround = requests.length > 0
      ? requests.reduce((sum, r) => {
          const days = (new Date(r.updated_at).getTime() - new Date(r.created_at).getTime()) / (1000 * 60 * 60 * 24);
          return sum + days;
        }, 0) / requests.length
      : 0;
    const completionRate = requests.length > 0 ? Math.round((confirmedCount / requests.length) * 100) : 0;

    const typeMap: Record<string, number> = {};
    requests.forEach((r) => { typeMap[r.service_type] = (typeMap[r.service_type] || 0) + 1; });
    const serviceBreakdown = Object.entries(typeMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([type, count]) => ({ 
        type, 
        count, 
        pct: requests.length > 0 ? Math.round((count / requests.length) * 100) : 0 
      }));

    const statusDist = STATUS_META.map((s) => ({
      label: s.label,
      status: s.status,
      color: s.color,
      count: requests.filter((r) => r.status === s.status).length,
    }));

    return { totalSpend, avgTurnaround, completionRate, serviceBreakdown, statusDist };
  }, [requests]);

  const assignedSurveyors = useMemo(
    () =>
      requests
        .filter((request) => request.assigned_surveyor)
        .map((request) => request.assigned_surveyor!)
        .filter((surveyor, index, list) => list.findIndex((item) => item.id === surveyor.id) === index)
        .sort((a, b) =>
          toNumber(b.rating_average) - toNumber(a.rating_average) ||
          toNumber(b.total_jobs) - toNumber(a.total_jobs)
        )
        .slice(0, 8),
    [requests],
  );

  const kpiCards = [
    { label: 'Total Req', count: requests.length, icon: 'clipboard' as IconName, color: 'var(--sc-brand-primary)' },
    ...STATUS_META.map((s) => ({
      label: s.shortLabel,
      count: countFor(s.status),
      icon: s.icon,
      color: s.color,
    })),
    { label: 'Deal Value', count: `$${analytics.totalSpend.toFixed(0)}`, icon: 'money' as IconName, color: 'var(--sc-brand-primary)' },
    { label: 'Comp. %', count: `${analytics.completionRate}%`, icon: 'target' as IconName, color: '#0EA5E9' },
  ];

  if (isMobile) {
    const recentRequests = filtered.slice(0, 5);

    return (
      <div style={styles.mobileContainer}>
        {/* Welcome Header */}
        <div style={styles.mobileWelcome}>
          <div>
            <span style={styles.welcomeTitleMobile}>Welcome back,</span>
            <h2 style={styles.welcomeNameMobile}>{user?.first_name || 'Client'}</h2>
          </div>
          <div style={styles.welcomeBadgeMobile}>
            Client
          </div>
        </div>

        {/* Dynamic sliding panel */}
        <div style={styles.mobileSheet}>
          {/* Quick Actions Row */}
          <div style={styles.actionRowMobile}>
            <Link to="/client/request/create" style={styles.actionItemMobile}>
              <div style={styles.actionCircleMobile}>
                <AnimatedIcon name="pen" size={20} color="#FFF" />
              </div>
              <span style={styles.actionLabelMobile}>Create</span>
            </Link>
            <Link to="/client/categories" style={styles.actionItemMobile}>
              <div style={styles.actionCircleMobile}>
                <AnimatedIcon name="folder" size={20} color="#FFF" />
              </div>
              <span style={styles.actionLabelMobile}>Categories</span>
            </Link>
            <Link to="/client/history" style={styles.actionItemMobile}>
              <div style={styles.actionCircleMobile}>
                <AnimatedIcon name="clipboard" size={20} color="#FFF" />
              </div>
              <span style={styles.actionLabelMobile}>History</span>
            </Link>
            <Link to="/client/messages" style={styles.actionItemMobile}>
              <div style={styles.actionCircleMobile}>
                <AnimatedIcon name="chat" size={20} color="#FFF" />
              </div>
              <span style={styles.actionLabelMobile}>Inbox</span>
            </Link>
          </div>

          <div style={styles.sectionDivider} />

          {/* Quick Contacts Feed */}
          <div style={{ marginBottom: 24 }}>
            <h3 style={styles.mobileSecTitle}>Active Surveyors</h3>
            {assignedSurveyors.length === 0 ? (
              <div style={styles.emptyActiveSurveyors}>
                Assigned surveyors will appear here after a job is allocated.
              </div>
            ) : (
              <div style={styles.avatarScroll}>
                {assignedSurveyors.map((surveyor) => {
                  const name = getSurveyorDisplayName(surveyor);
                  return (
                    <div
                      key={surveyor.id}
                      style={styles.avatarCol}
                      onClick={() => navigate(`/client/surveyor/${surveyor.id}`)}
                    >
                      <div style={styles.avatarCircle}>
                        <span style={styles.avatarCircleText}>{(name[0] || 'S').toUpperCase()}</span>
                      </div>
                      <span style={styles.avatarName}>{name}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recent Activity List */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ ...styles.mobileSecTitle, margin: 0 }}>Recent Activity</h3>
              <Link to="/client/history" style={{ fontSize: 13, fontWeight: 700, color: 'var(--sc-brand-primary)', textDecoration: 'none' }}>See all</Link>
            </div>
            
            <div style={styles.activityList}>
              {recentRequests.map((req) => (
                <Link to={`/client/request/${req.id}`} key={req.id} style={styles.activityItem} className="sc-hover-scale">
                  <div style={styles.activityIcon}>
                    <AnimatedIcon name="clipboard" size={16} color="var(--sc-brand-primary)" />
                  </div>
                  <div style={styles.activityInfo}>
                    <span style={styles.activityTitle}>{req.service_type}</span>
                    <span style={styles.activityDate}>
                      {new Date(req.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} • 
                      <span style={{ fontWeight: 600, color: STATUS_COLORS[req.status]?.color || 'var(--sc-text-secondary)', marginLeft: 4 }}>
                        {getCompactStatusText(req.status)}
                      </span>
                    </span>
                  </div>
                  <div style={styles.activityValue}>
                    ${(req.deal_value || 0).toFixed(2)}
                  </div>
                </Link>
              ))}
              {recentRequests.length === 0 && (
                <div style={{ textAlign: 'center', padding: 32, color: 'var(--sc-text-secondary)', fontSize: 14 }}>
                  No active requests. Get started by clicking Create.
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    );
  }

  // Desktop View
  return (
    <div>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.pageTitle}>Dashboard</h1>
          <p style={styles.pageSubtitle}>Welcome back, {user?.first_name}. Track your survey project requests and finances.</p>
        </div>
        <Link to="/client/request/create" style={styles.ctaBtn} className="sc-press">+ Create Request</Link>
      </div>

      {/* KPI Row */}
      <div style={styles.kpiGrid}>
        {kpiCards.map((kpi) => (
          <div key={kpi.label} style={styles.kpiCard}>
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 3,
              background: kpi.color
            }} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, position: 'relative', zIndex: 1 }}>
              <div style={{ ...styles.kpiIconBox, background: 'var(--sc-bg-input)', color: kpi.color }}>
                <AnimatedIcon name={kpi.icon} size={16} color={kpi.color} />
              </div>
              <span style={styles.kpiLabel}>{kpi.label}</span>
              <span style={styles.kpiValue}>{kpi.count}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Analytics Grid */}
      <div style={styles.analyticsGrid}>
        {/* Service breakdown */}
        <div style={styles.analyticCard}>
          <h3 style={styles.analyticTitle}>Service Type Breakdown</h3>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 140 }}>
            {analytics.serviceBreakdown.length > 0 ? (
              <PieChart
                data={analytics.serviceBreakdown.map((sb, i) => ({ label: sb.type, value: sb.count, color: PIE_COLORS[i % PIE_COLORS.length] }))}
                size={110}
                ringWidth={0}
                centerSub="types"
              />
            ) : (
              <p style={{ color: 'var(--sc-text-secondary)', fontSize: 13 }}>No requests logged yet.</p>
            )}
          </div>
        </div>

        {/* Status Distribution */}
        <div style={styles.analyticCard}>
          <h3 style={styles.analyticTitle}>Status Distribution</h3>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 140 }}>
            {analytics.statusDist.length > 0 ? (
              <PieChart
                data={analytics.statusDist.map((sd) => ({ label: sd.label, value: sd.count, color: sd.color }))}
                size={90}
                ringWidth={22}
                centerSub="requests"
              />
            ) : (
              <p style={{ color: 'var(--sc-text-secondary)', fontSize: 13 }}>No requests logged yet.</p>
            )}
          </div>
        </div>
      </div>

      <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '28px 0 16px', fontFamily: 'var(--sc-font-display)' }}>Top Specialists For You</h2>
      {topSurveyors.length === 0 ? (
        <div style={styles.emptySpecialists}>
          <AnimatedIcon name="star" size={32} color="var(--sc-text-tertiary)" />
          <span>No rated surveyors yet. Completed ratings will appear here.</span>
        </div>
      ) : (
        <div style={styles.specialistGrid}>
          {topSurveyors.map((surveyor, index) => (
            <button
              key={surveyor.id}
              type="button"
              style={styles.specialistCard}
              onClick={() => navigate(`/client/surveyor/${surveyor.id}`)}
            >
              <div style={styles.specialistTop}>
                <div style={styles.specialistAvatar}>{(surveyor.first_name?.[0] || 'S').toUpperCase()}</div>
                <span style={styles.rankPill}>{toNumber(surveyor.rating_average) >= 4.8 ? 'Platinum' : index === 0 ? 'Top Rated' : 'Specialist'}</span>
              </div>
              <strong style={styles.specialistName}>{surveyor.first_name} {surveyor.last_name}</strong>
              <span style={styles.specialistService}>{surveyor.service_types?.[0] || 'Survey specialist'}</span>
              <div style={styles.specialistStats}>
                <span>★ {toNumber(surveyor.rating_average).toFixed(1)}</span>
                <span>{toNumber(surveyor.total_jobs)} jobs</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Request list filters & search */}
      <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '28px 0 16px', fontFamily: 'var(--sc-font-display)' }}>Request List</h2>
      <div style={styles.filterRow}>
        <div style={styles.tabs}>
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.label}
              onClick={() => setActiveFilter(tab.value)}
              style={{
                ...styles.tab,
                background: activeFilter === tab.value ? 'var(--sc-brand-primary-bg)' : 'transparent',
                color: activeFilter === tab.value ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)',
                fontWeight: activeFilter === tab.value ? 700 : 500,
                border: activeFilter === tab.value ? '1px solid var(--sc-brand-primary-light)' : '1px solid transparent',
              }}
            >
              {tab.label}
              <span style={{
                ...styles.tabBadge,
                background: activeFilter === tab.value ? 'var(--sc-brand-primary)' : 'var(--sc-bg-hover)',
                color: activeFilter === tab.value ? 'var(--sc-text-inverse)' : 'var(--sc-text-secondary)',
              }}>{countFor(tab.value)}</span>
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="🔍 Search requests..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Advanced filters */}
      <div style={styles.advFilterRow}>
        <select
          value={serviceTypeFilter}
          onChange={(e) => setServiceTypeFilter(e.target.value)}
          style={styles.advSelect}
        >
          <option value="">All Service Types</option>
          {SURVEY_SERVICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div style={styles.dateGroup}>
          <label style={styles.dateLabel}>From:</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.dateInput} />
        </div>
        <div style={styles.dateGroup}>
          <label style={styles.dateLabel}>To:</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.dateInput} />
        </div>
        {(serviceTypeFilter || dateFrom || dateTo) && (
          <button onClick={() => { setServiceTypeFilter(''); setDateFrom(''); setDateTo(''); }} style={styles.clearBtn}>✕ Clear</button>
        )}
      </div>

      {/* Table */}
      {isLoading && requests.length === 0 ? (
        <div style={styles.loadingBox}>
          <div style={styles.spinner} />
          <p style={{ color: 'var(--sc-text-secondary)' }}>Loading requests...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={styles.emptyBox}>
          <AnimatedIcon name="clipboard" size={48} color="var(--sc-text-tertiary)" />
          <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '12px 0 6px' }}>No requests found</h3>
          <p style={{ fontSize: 14, color: 'var(--sc-text-secondary)', margin: '0 0 20px' }}>Create your first survey request to get started.</p>
          <Link to="/client/request/create" style={styles.ctaBtn}>+ New Request</Link>
        </div>
      ) : (
        <div style={styles.table}>
          <div style={styles.tableHead}>
            <div style={{ ...styles.th, flex: 2 }}>Service Type</div>
            <div style={{ ...styles.th, flex: 3 }}>Description</div>
            <div style={{ ...styles.th, flex: 2 }}>Location</div>
            <div style={styles.th}>Status</div>
            <div style={styles.th}>Date</div>
            <div style={{ ...styles.th, flex: 0.5 }}></div>
          </div>
          {filtered.map((req) => (
            <Link to={`/client/request/${req.id}`} key={req.id} style={styles.tableRow}>
              <div style={{ ...styles.td, flex: 2, fontWeight: 600 }}>{req.service_type}</div>
              <div style={{ ...styles.td, flex: 3, color: 'var(--sc-text-secondary)' }}>{req.description.substring(0, 80)}{req.description.length > 80 ? '...' : ''}</div>
              <div style={{ ...styles.td, flex: 2, color: 'var(--sc-text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <AnimatedIcon name="bell" size={13} color="var(--sc-text-tertiary)" /> 
                <span>{req.address.substring(0, 40)}</span>
              </div>
              <div style={styles.td}>
                <span style={{
                  ...styles.badge,
                  background: STATUS_COLORS[req.status]?.bg || 'var(--sc-bg-input)',
                  color: STATUS_COLORS[req.status]?.color || 'var(--sc-text-secondary)',
                  borderColor: STATUS_COLORS[req.status]?.border || 'var(--sc-border)',
                }}>{getCompactStatusText(req.status)}</span>
              </div>
              <div style={{ ...styles.td, color: 'var(--sc-text-secondary)', fontSize: 12 }}>{formatDate(req.created_at)}</div>
              <div style={{ ...styles.td, flex: 0.5, textAlign: 'center', color: 'var(--sc-brand-primary)' }}>→</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 16 },
  pageTitle: { fontSize: 28, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', letterSpacing: '-0.5px', fontFamily: "var(--sc-font-display)" },
  pageSubtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: 0 },
  ctaBtn: { display: 'inline-flex', padding: '10px 20px', background: 'var(--sc-brand-gradient)', color: 'var(--sc-text-on-brand)', borderRadius: 'var(--sc-radius-md)', fontSize: 13.5, fontWeight: 700, textDecoration: 'none', border: 'none', cursor: 'pointer', boxShadow: 'var(--sc-shadow-brand)', transition: 'all var(--sc-transition-fast)' },

  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 },
  kpiCard: { background: 'var(--sc-bg-secondary)', borderRadius: 'var(--sc-radius-lg)', padding: '20px 16px', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-card)', textAlign: 'center', position: 'relative', overflow: 'hidden' },
  kpiIconBox: { width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' },
  kpiLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' },
  kpiValue: { fontSize: 20, fontWeight: 800, color: 'var(--sc-text-primary)', marginTop: 4 },

  analyticsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 24 },
  analyticCard: { background: 'var(--sc-bg-secondary)', borderRadius: 'var(--sc-radius-lg)', padding: 24, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-card)' },
  analyticTitle: { fontSize: 15, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 16px', fontFamily: "var(--sc-font-display)", letterSpacing: '-0.1px' },

  specialistGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12, marginBottom: 24 },
  specialistCard: { textAlign: 'left', border: '1px solid var(--sc-border)', borderRadius: 'var(--sc-radius-lg)', background: 'var(--sc-bg-secondary)', padding: 16, cursor: 'pointer', boxShadow: 'var(--sc-shadow-card)', fontFamily: 'inherit' },
  specialistTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  specialistAvatar: { width: 42, height: 42, borderRadius: 10, background: 'var(--sc-bg-active)', color: 'var(--sc-brand-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 900 },
  rankPill: { fontSize: 10, fontWeight: 800, color: 'var(--sc-brand-primary)', background: 'var(--sc-brand-primary-bg)', borderRadius: 999, padding: '4px 8px', textTransform: 'uppercase' },
  specialistName: { display: 'block', color: 'var(--sc-text-primary)', fontSize: 14, marginBottom: 4 },
  specialistService: { display: 'block', color: 'var(--sc-text-secondary)', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  specialistStats: { display: 'flex', gap: 10, marginTop: 12, color: 'var(--sc-text-secondary)', fontSize: 12, fontWeight: 700 },
  emptySpecialists: { display: 'flex', alignItems: 'center', gap: 10, border: '1px solid var(--sc-border)', borderRadius: 'var(--sc-radius-lg)', background: 'var(--sc-bg-secondary)', padding: 18, color: 'var(--sc-text-secondary)', fontSize: 13, marginBottom: 24 },

  filterRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 16, flexWrap: 'wrap' },
  tabs: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  tab: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 'var(--sc-radius-sm)', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', transition: 'all var(--sc-transition-fast)' },
  tabBadge: { fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '2px 6px', minWidth: 18, textAlign: 'center' },
  searchInput: { padding: '10px 16px', border: '1px solid var(--sc-border)', borderRadius: 'var(--sc-radius-md)', fontSize: 13.5, outline: 'none', minWidth: 240, fontFamily: 'inherit', color: 'var(--sc-text-primary)', transition: 'all var(--sc-transition-fast)', background: 'var(--sc-bg-input)' },

  advFilterRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' },
  advSelect: { padding: '9px 14px', border: '1px solid var(--sc-border)', borderRadius: 'var(--sc-radius-md)', fontSize: 13, outline: 'none', fontFamily: 'inherit', background: 'var(--sc-bg-input)', color: 'var(--sc-text-primary)', minWidth: 180 },
  dateGroup: { display: 'flex', alignItems: 'center', gap: 8 },
  dateLabel: { fontSize: 12, fontWeight: 600, color: 'var(--sc-text-secondary)' },
  dateInput: { padding: '8px 12px', border: '1px solid var(--sc-border)', borderRadius: 'var(--sc-radius-sm)', fontSize: 13, outline: 'none', fontFamily: 'inherit', color: 'var(--sc-text-primary)', background: 'var(--sc-bg-input)' },
  clearBtn: { padding: '8px 14px', background: 'var(--sc-error-bg)', color: 'var(--sc-error-text)', border: 'none', borderRadius: 'var(--sc-radius-sm)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },

  table: { background: 'var(--sc-bg-secondary)', borderRadius: 'var(--sc-radius-lg)', border: '1px solid var(--sc-border)', overflow: 'hidden', boxShadow: 'var(--sc-shadow-card)' },
  tableHead: { display: 'flex', padding: '12px 20px', background: 'var(--sc-bg-hover)', borderBottom: '1px solid var(--sc-border)' },
  th: { flex: 1, fontSize: 11, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 },
  tableRow: { display: 'flex', padding: '16px 20px', borderBottom: '1px solid var(--sc-divider)', textDecoration: 'none', cursor: 'pointer', transition: 'all var(--sc-transition-fast)', alignItems: 'center' },
  td: { flex: 1, fontSize: 13.5, color: 'var(--sc-text-primary)' },
  badge: { display: 'inline-block', padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', border: '1px solid' },

  loadingBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'sc-spin 0.8s linear infinite', marginBottom: 12 },
  emptyBox: { textAlign: 'center', padding: 60, background: 'var(--sc-bg-secondary)', borderRadius: 'var(--sc-radius-lg)', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-card)' },

  // Mobile Styles
  mobileContainer: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--sc-bg-hero)' },
  mobileWelcome: {
    padding: '24px 20px 20px',
    color: '#FFFFFF',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  welcomeTitleMobile: { fontSize: 13, color: 'rgba(255, 255, 255, 0.7)', fontWeight: 500 },
  welcomeNameMobile: { fontSize: 22, fontWeight: 800, margin: '4px 0 0 0', letterSpacing: '-0.5px', fontFamily: "var(--sc-font-display)" },
  welcomeBadgeMobile: {
    background: 'rgba(255, 255, 255, 0.15)',
    color: '#FFFFFF',
    padding: '4px 12px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    border: '1px solid rgba(255, 255, 255, 0.25)',
  },

  mobileSheet: {
    background: 'var(--sc-bg-primary)',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: '24px 20px 80px', // space for tab bar
    flex: 1,
    boxShadow: 'var(--sc-shadow-xl)',
  },
  actionRowMobile: { display: 'flex', justifyContent: 'space-around', alignItems: 'center', marginBottom: 20 },
  actionItemMobile: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textDecoration: 'none', width: 60 },
  actionCircleMobile: { width: 48, height: 48, borderRadius: 24, background: 'var(--sc-brand-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--sc-shadow-brand)' },
  actionLabelMobile: { fontSize: 11, fontWeight: 600, color: 'var(--sc-text-primary)' },
  sectionDivider: { height: 1, background: 'var(--sc-border-light)', margin: '0 0 20px 0' },
  mobileSecTitle: { fontSize: 14, fontWeight: 800, color: 'var(--sc-text-primary)', marginBottom: 12, fontFamily: "var(--sc-font-display)" },
  avatarScroll: { display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 8, scrollbarWidth: 'none' },
  avatarCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer', minWidth: 64 },
  avatarCircle: { width: 48, height: 48, borderRadius: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--sc-shadow-sm)', background: 'var(--sc-brand-primary-bg)' },
  avatarCircleText: { fontSize: 16, fontWeight: 700, color: 'var(--sc-brand-primary)' },
  avatarName: { fontSize: 11, fontWeight: 600, color: 'var(--sc-text-secondary)', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: 60 },
  emptyActiveSurveyors: { border: '1px solid var(--sc-border)', borderRadius: 14, background: 'var(--sc-bg-secondary)', color: 'var(--sc-text-secondary)', fontSize: 12, lineHeight: 1.5, padding: '12px 14px' },
  activityList: { display: 'flex', flexDirection: 'column', gap: 8 },
  activityItem: { display: 'flex', alignItems: 'center', padding: '12px 14px', background: 'var(--sc-bg-secondary)', borderRadius: 14, border: '1px solid var(--sc-border)', textDecoration: 'none', gap: 12 },
  activityIcon: { width: 40, height: 40, borderRadius: 20, background: 'var(--sc-bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  activityInfo: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  activityTitle: { fontSize: 13, fontWeight: 700, color: 'var(--sc-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  activityDate: { fontSize: 11, color: 'var(--sc-text-secondary)', marginTop: 2 },
  activityValue: { fontSize: 14, fontWeight: 800, color: 'var(--sc-text-primary)' },
};
